import { useState } from 'react';
import { AppLayout } from '@/components/layout/AppLayout';
import { Button } from '@/components/ui/button';
import {
 CreditCard,
 CheckCircle,
 AlertTriangle,
 Download,
 Zap,
 FileText,
} from 'lucide-react';

export function BillingPage() {
 // Toggle for demo purposes
 const [hasSubscription, setHasSubscription] = useState(false);

 return (
  <AppLayout>
   <div className='flex items-center justify-between mb-12 border-b border-border pb-8'>
    <div>
     <div className='flex items-center gap-2 mb-2'>
      <CreditCard className='w-4 h-4 text-primary' />
      <span className='font-mono text-xs text-primary'>
       RESOURCE_ALLOCATION
      </span>
     </div>
     <h1 className='text-3xl font-bold font-mono tracking-tight mb-2'>
      SUBSCRIPTION & BILLING
     </h1>
     <p className='text-muted-foreground font-mono text-sm max-w-2xl'>
      Manage your system access, payment methods, and invoice history.
     </p>
    </div>

    {/* Demo Toggle */}
    <div className='flex items-center gap-2 bg-muted/20 p-2 border border-border/50'>
     <span className='text-[10px] font-mono text-muted-foreground uppercase'>
      Preview State:
     </span>
     <Button
      variant={hasSubscription ? 'default' : 'outline'}
      size='sm'
      onClick={() => setHasSubscription(true)}
      className='h-6 text-[10px] rounded-none font-mono'
     >
      ACTIVE
     </Button>
     <Button
      variant={!hasSubscription ? 'default' : 'outline'}
      size='sm'
      onClick={() => setHasSubscription(false)}
      className='h-6 text-[10px] rounded-none font-mono'
     >
      EMPTY
     </Button>
    </div>
   </div>

   {hasSubscription ? (
    <ActiveState />
   ) : (
    <EmptyState onSubscribe={() => setHasSubscription(true)} />
   )}
  </AppLayout>
 );
}

function EmptyState({ onSubscribe }: { onSubscribe: () => void }) {
 return (
  <div className='max-w-4xl mx-auto'>
   {/* Status Card */}
   <div className='border border-yellow-500/30 bg-yellow-500/5 p-8 mb-8'>
    <div className='flex items-start gap-4'>
     <AlertTriangle className='w-6 h-6 text-yellow-500 mt-1' />
     <div>
      <h3 className='text-xl font-bold font-mono text-yellow-500 mb-2'>
       SYSTEM_ACCESS_LIMITED
      </h3>
      <p className='text-muted-foreground max-w-xl mb-6'>
       Your free tier allocation has been exhausted. System capabilities are
       currently restricted to basic text processing only. Upgrade to restore
       full neural network access.
      </p>
      <div className='grid grid-cols-2 gap-4 max-w-lg mb-6'>
       <div className='flex items-center gap-2 text-sm text-muted-foreground font-mono'>
        <div className='w-2 h-2 bg-red-500 rounded-full' />
        <span>VOICE_SYNTHESIS: OFF</span>
       </div>
       <div className='flex items-center gap-2 text-sm text-muted-foreground font-mono'>
        <div className='w-2 h-2 bg-red-500 rounded-full' />
        <span>CODE_EXECUTION: OFF</span>
       </div>
       <div className='flex items-center gap-2 text-sm text-muted-foreground font-mono'>
        <div className='w-2 h-2 bg-red-500 rounded-full' />
        <span>ADVANCED_MODELS: OFF</span>
       </div>
      </div>
     </div>
    </div>
   </div>

   {/* Pricing */}
   <div className='grid md:grid-cols-2 gap-8'>
    <div className='border border-border bg-background p-8 flex flex-col opacity-60'>
     <h3 className='font-mono text-lg font-bold mb-4'>FREE TIER</h3>
     <div className='text-3xl font-bold mb-6 font-mono'>
      $0
      <span className='text-sm text-muted-foreground font-sans font-normal'>
       /mo
      </span>
     </div>
     <ul className='space-y-3 mb-8 flex-1'>
      <li className='flex items-center gap-2 text-sm'>
       <CheckCircle className='w-4 h-4' /> 1 Interview / Month
      </li>
      <li className='flex items-center gap-2 text-sm'>
       <CheckCircle className='w-4 h-4' /> Basic Feedback
      </li>
     </ul>
     <Button
      disabled
      variant='outline'
      className='w-full rounded-none font-mono'
     >
      CURRENT PLAN
     </Button>
    </div>

    <div className='border border-primary bg-primary/5 p-8 flex flex-col relative overflow-hidden'>
     <div className='absolute top-0 right-0 bg-primary text-primary-foreground text-[10px] font-mono px-2 py-1 uppercase'>
      Recommended
     </div>
     <h3 className='font-mono text-lg font-bold mb-4 text-primary'>
      PRO LICENSE
     </h3>
     <div className='text-3xl font-bold mb-6 font-mono'>
      $29
      <span className='text-sm text-muted-foreground font-sans font-normal'>
       /mo
      </span>
     </div>
     <ul className='space-y-3 mb-8 flex-1'>
      <li className='flex items-center gap-2 text-sm'>
       <CheckCircle className='w-4 h-4 text-primary' /> Unlimited Interviews
      </li>
      <li className='flex items-center gap-2 text-sm'>
       <CheckCircle className='w-4 h-4 text-primary' /> GPT-4 Turbo Access
      </li>
      <li className='flex items-center gap-2 text-sm'>
       <CheckCircle className='w-4 h-4 text-primary' /> Voice Mode
      </li>
      <li className='flex items-center gap-2 text-sm'>
       <CheckCircle className='w-4 h-4 text-primary' /> Detailed Analytics
      </li>
     </ul>
     <Button
      onClick={onSubscribe}
      className='w-full rounded-none font-mono uppercase font-bold tracking-widest'
     >
      UPGRADE_SYSTEM
     </Button>
    </div>
   </div>
  </div>
 );
}

function ActiveState() {
 const invoices = [
  { id: 'INV-2024-03', date: 'Mar 01, 2024', amount: '$29.00', status: 'PAID' },
  { id: 'INV-2024-02', date: 'Feb 01, 2024', amount: '$29.00', status: 'PAID' },
  { id: 'INV-2024-01', date: 'Jan 01, 2024', amount: '$29.00', status: 'PAID' },
 ];

 return (
  <div className='max-w-4xl mx-auto'>
   {/* Status Card */}
   <div className='border border-primary/30 bg-primary/5 p-8 mb-8'>
    <div className='flex items-start gap-4'>
     <Zap className='w-6 h-6 text-primary mt-1' />
     <div className='w-full'>
      <div className='flex justify-between items-start'>
       <div>
        <h3 className='text-xl font-bold font-mono text-primary mb-2'>
         PRO_LICENSE_ACTIVE
        </h3>
        <p className='text-muted-foreground text-sm font-mono mb-4'>
         Next billing cycle: April 01, 2024
        </p>
       </div>
       <Button
        variant='outline'
        size='sm'
        className='rounded-none font-mono text-xs'
       >
        MANAGE SUBSCRIPTION
       </Button>
      </div>

      <div className='grid grid-cols-3 gap-4 mt-4'>
       <div className='bg-background/50 border border-border p-4'>
        <div className='text-[10px] font-mono text-muted-foreground uppercase mb-1'>
         Api Calls
        </div>
        <div className='text-2xl font-mono font-bold'>842</div>
       </div>
       <div className='bg-background/50 border border-border p-4'>
        <div className='text-[10px] font-mono text-muted-foreground uppercase mb-1'>
         Compute Hours
        </div>
        <div className='text-2xl font-mono font-bold'>12.4</div>
       </div>
       <div className='bg-background/50 border border-border p-4'>
        <div className='text-[10px] font-mono text-muted-foreground uppercase mb-1'>
         Storage
        </div>
        <div className='text-2xl font-mono font-bold'>45MB</div>
       </div>
      </div>
     </div>
    </div>
   </div>

   {/* Invoice History */}
   <div className='border border-border bg-background p-8'>
    <h3 className='font-mono text-sm font-bold mb-6 flex items-center gap-2'>
     <FileText className='w-4 h-4' />
     INVOICE_HISTORY
    </h3>
    <div className='space-y-1'>
     {invoices.map((invoice) => (
      <div
       key={invoice.id}
       className='grid grid-cols-4 items-center p-4 border border-border/50 hover:bg-muted/10 transition-colors font-mono text-sm'
      >
       <div>{invoice.id}</div>
       <div className='text-muted-foreground'>{invoice.date}</div>
       <div>{invoice.amount}</div>
       <div className='flex justify-end gap-4 items-center'>
        <span className='bg-green-500/10 text-green-500 px-2 py-0.5 text-[10px]'>
         {invoice.status}
        </span>
        <Button variant='ghost' size='icon' className='h-8 w-8 rounded-none'>
         <Download className='w-3 h-3' />
        </Button>
       </div>
      </div>
     ))}
    </div>
   </div>
  </div>
 );
}
